import React from 'react';
import Layout from '../components/Layout';
import api from '../services/api';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

class Reports extends React.Component {
  state = {
    from: firstOfMonthIso(),
    to: todayIso(),
    company_id: '',
    companies: [],
    rows: [],
    summary: null,
    loading: false,
    error: '',
    exporting: false,
    hasSearched: false
  };

  componentDidMount() {
    this.loadCompanies();
    this.runSearch();
  }

  loadCompanies = async () => {
    try {
      const res = await api.get('/companies');
      this.setState({ companies: res.data });
    } catch (err) {
      // Non-fatal — the report still works without the company filter list
    }
  };

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  runSearch = async () => {
    const { from, to, company_id } = this.state;
    if (from && to && from > to) {
      this.setState({ error: '"From" date must be before "To" date.' });
      return;
    }

    this.setState({ loading: true, error: '' });
    try {
      const res = await api.get('/reports/invoices', { params: { from, to, company_id } });
      this.setState({ rows: res.data.rows, summary: res.data.summary, loading: false, hasSearched: true });
    } catch (err) {
      this.setState({ error: 'Failed to load report.', loading: false });
    }
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.runSearch();
  };

  handleExport = async () => {
    const { from, to, company_id } = this.state;
    this.setState({ exporting: true, error: '' });
    try {
      const res = await api.get('/reports/invoices/export', {
        params: { from, to, company_id },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      const label = from || to ? `_${from || 'start'}_to_${to || 'end'}` : '';
      link.download = `invoice-report${label}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      this.setState({ error: 'Failed to export Excel file.' });
    } finally {
      this.setState({ exporting: false });
    }
  };

  render() {
    const { from, to, company_id, companies, rows, summary, loading, error, exporting, hasSearched } = this.state;

    return (
      <Layout>
        <div className="page-header">
          <div>
            <h1 className="page-title">Reports</h1>
            <p className="page-sub">Filter invoices by date range and export to Excel</p>
          </div>
          <button className="btn btn-brass" onClick={this.handleExport} disabled={exporting || rows.length === 0}>
            {exporting ? 'Exporting…' : 'Download Excel'}
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="card">
          <form onSubmit={this.handleSubmit} className="form-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field">
              <label>From Date</label>
              <input type="date" name="from" value={from} onChange={this.handleChange} />
            </div>
            <div className="field">
              <label>To Date</label>
              <input type="date" name="to" value={to} onChange={this.handleChange} />
            </div>
            <div className="field">
              <label>Company</label>
              <select name="company_id" value={company_id} onChange={this.handleChange}>
                <option value="">All companies</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: '0 0 auto' }}>
              <button type="submit" className="btn btn-outline" disabled={loading}>
                {loading ? 'Searching…' : 'Search'}
              </button>
            </div>
          </form>
        </div>

        {summary && (
          <div className="wizard-summary" style={{ marginBottom: 16 }}>
            <span className="chip">{summary.count} invoice{summary.count === 1 ? '' : 's'}</span>
            <span className="chip">Subtotal: ₹{summary.subtotal.toFixed(2)}</span>
            <span className="chip">Tax: ₹{summary.tax.toFixed(2)}</span>
            <span className="chip">Total: ₹{summary.total.toFixed(2)}</span>
          </div>
        )}

        <div className="card">
          {loading ? (
            <p>Loading...</p>
          ) : !hasSearched ? (
            <p>Choose a date range and click Search.</p>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <div className="glyph">—</div>
              No invoices match these filters.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Company</th>
                  <th>Hotel</th>
                  <th>Hotel GST No</th>
                  <th>Type</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono" data-label="Invoice #">{r.invoice_number}</td>
                    <td data-label="Date">{r.invoice_date}</td>
                    <td data-label="Company">{r.company_name || '—'}</td>
                    <td data-label="Hotel">{r.hotel_name || '—'}</td>
                    <td className="mono" data-label="Hotel GST No">{r.hotel_gst_number || '—'}</td>
                    <td data-label="Type">{r.invoice_type === 'bulk_booking' ? 'Bulk Booking' : r.invoice_type === 'guest_entry' ? 'Guest Entry' : '—'}</td>
                    <td className="amount" data-label="Total">₹{Number(r.total_amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Layout>
    );
  }
}

export default Reports;